(() => {
  if (window.daylaWholesaleCartReady) return;
  window.daylaWholesaleCartReady = true;

  const config = window.DaylaWholesaleConfig;
  const lineSelector = '[data-dayla-cart-line]';
  const renderedSignatures = new WeakMap();

  const getGroups = (scope = document) => {
    const groups = new Map();
    scope.querySelectorAll(lineSelector).forEach((line) => {
      const input = line.querySelector('.quantity__input');
      if (!input) return;
      const key = line.dataset.daylaGroup;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          color: line.dataset.daylaColor || 'Default',
          product: line.dataset.daylaProductTitle || '',
          minimum: Number(line.dataset.daylaMoq) || config.defaultMinimumQuantity,
          variantCount: Number(line.dataset.daylaGroupVariantCount) || 1,
          lines: [],
        });
      }
      groups.get(key).lines.push({
        element: line,
        input,
        quantity: Number(input.value) || 0,
        lineKey: line.dataset.daylaLineKey,
      });
    });
    return groups;
  };

  const renderGroupHeaders = (scope, groups) => {
    const signature = [...groups.values()]
      .map((group) => `${group.key}:${group.lines.map((line) => line.quantity).join(',')}`)
      .join('|');
    if (renderedSignatures.get(scope) === signature && scope.querySelector('.dayla-cart-group-header')) return;
    renderedSignatures.set(scope, signature);
    scope.querySelectorAll('.dayla-cart-group-header').forEach((header) => header.remove());
    groups.forEach((group) => {
      const total = group.lines.reduce((sum, line) => sum + line.quantity, 0);
      const valid = total === 0 || total >= group.minimum;
      const firstLine = group.lines[0].element;
      const row = document.createElement('tr');
      row.className = 'dayla-cart-group-header';
      row.innerHTML = `
        <td colspan="${firstLine.dataset.daylaColspan || 5}">
          <div class="dayla-cart-group-header__content${valid ? '' : ' is-invalid'}">
            <span>${escapeHtml(group.product)} · ${escapeHtml(group.color)} · ${total} units · ${
              valid ? 'MOQ met' : `Add ${group.minimum - total} more`
            }</span>
            <button type="button" data-dayla-remove-group="${escapeHtml(group.key)}">Remove color</button>
          </div>
        </td>`;
      firstLine.before(row);
    });
  };

  const updateScope = (root) => {
    const groups = getGroups(root);
    renderGroupHeaders(root, groups);
    const invalidGroups = [...groups.values()].filter((group) => {
      const total = group.lines.reduce((sum, line) => sum + line.quantity, 0);
      return total > 0 && total < group.minimum;
    });

    root.querySelectorAll('[data-dayla-cart-validation]').forEach((message) => {
      message.classList.toggle('is-invalid', invalidGroups.length > 0);
      message.textContent = invalidGroups.length
        ? `${invalidGroups.length} Color group${invalidGroups.length === 1 ? '' : 's'} still need MOQ attention.`
        : 'All selected Color groups meet MOQ.';
    });

    root.querySelectorAll('[data-dayla-progress]').forEach((progress) => {
      const subtotal = Number(progress.dataset.subtotal) || 0;
      const remaining = Math.max(0, config.storeMinimumCents - subtotal);
      const percentage = Math.min(100, (subtotal / config.storeMinimumCents) * 100);
      progress.querySelector('[data-dayla-progress-bar]').style.width = `${percentage}%`;
      progress.querySelector('[data-dayla-store-minimum]').textContent = money(config.storeMinimumCents);
      progress.querySelector('[data-dayla-progress-message]').textContent = remaining
        ? `${money(remaining)} away from the ${money(config.storeMinimumCents)} minimum`
        : 'Store minimum reached.';
    });

    const subtotal = Number(root.querySelector('[data-dayla-progress]')?.dataset.subtotal || 0);
    const cartValid = subtotal >= config.storeMinimumCents && invalidGroups.length === 0 && groups.size > 0;
    root.querySelectorAll('[data-dayla-checkout]').forEach((button) => {
      button.disabled = !cartValid;
      button.setAttribute('aria-disabled', cartValid ? 'false' : 'true');
    });
  };

  const updateAll = () => {
    document.querySelectorAll('cart-drawer[data-dayla-cart-root]').forEach(updateScope);
    const mainContent = document.getElementById('MainContent');
    if (mainContent?.querySelector('[data-dayla-cart-page]')) updateScope(mainContent);
  };

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest(`${lineSelector} .quantity__input`);
      if (!input) return;
      const line = input.closest(lineSelector);
      const minimum = Number(line.dataset.daylaMoq) || config.defaultMinimumQuantity;
      const singleSize = Number(line.dataset.daylaGroupVariantCount) === 1;
      const previous = Number(input.getAttribute('value')) || 0;
      const next = Number(input.value) || 0;
      if (singleSize && next > 0 && next < minimum) {
        input.value = previous >= minimum ? 0 : minimum;
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const checkout = event.submitter?.matches('[data-dayla-checkout]');
      if (checkout && event.submitter.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-dayla-remove-group]');
    if (!button) return;
    const groups = getGroups(button.closest('cart-drawer') || document.getElementById('MainContent') || document);
    const group = groups.get(button.dataset.daylaRemoveGroup);
    if (!group) return;
    const updates = {};
    group.lines.forEach((line) => {
      updates[line.lineKey] = 0;
    });
    button.disabled = true;
    try {
      await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      window.location.reload();
    } catch {
      button.disabled = false;
    }
  });

  let updateQueued = false;
  const observer = new MutationObserver(() => {
    if (updateQueued) return;
    updateQueued = true;
    window.requestAnimationFrame(() => {
      updateQueued = false;
      updateAll();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', updateAll);
  updateAll();

  function money(cents) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(
      cents / 100
    );
  }

  function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  }
})();
